import Notification from '@rio-cloud/rio-uikit/Notification';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useGetOrderMaterialsQuery } from '#api/orderApi.slice.ts';
import { CancelDialog } from '#components/shared/CancelDialog.tsx';
import { Sidebar } from '#components/sidebar/Sidebar.tsx';
import { isServiceOrder, type Order } from '#domain/Order.ts';
import { DocumentPollingProvider } from '#features/documents/hooks/documentPolling.context.tsx';
import { useGetOrderDetailsDocumentsQuery } from '#features/documents/hooks/useGetOrderDetailsDocumentsQuery.ts';
import { useCancelOrder } from '#features/orderDetails/hooks/useCancelOrder.ts';
import { formatOrderSidebarName } from '#features/orderDetails/model/formatOrderSidebarName.ts';
import type { OrderWithSortableKey } from '#utils/orders.util.ts';

import { OrderDetails } from './OrderDetails.tsx';
import { OrderDetailsSidebarFooter } from './OrderDetailsSidebarFooter.tsx';

interface OrderDetailsSidebarProps {
    order: OrderWithSortableKey;
    selectedOrdersCount: number;
    skipPolling: boolean;
    setDocumentDialogState: (isDialogOpen: boolean) => void;
    handlePlanOrder: () => void;
    handleDuplicateOrder: () => void;
    handleEditOrder: () => void;
    close: () => void;
}

export const OrderDetailsSidebar = ({
    order,
    selectedOrdersCount,
    skipPolling,
    setDocumentDialogState,
    close,
    handlePlanOrder,
    handleDuplicateOrder,
    handleEditOrder,
}: OrderDetailsSidebarProps) => {
    const intl = useIntl();
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const { cancelOrder, isLoading } = useCancelOrder();

    const ordername =
        formatOrderSidebarName(order) ??
        intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.cancel.dialog.placeholder.order',
        });

    const handleCancel = async (tour: Order) => {
        return cancelOrder(tour.id).then(success => {
            if (success) {
                Notification.success(
                    intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.order.cancel.single.success',
                    })
                );
            } else {
                Notification.error(
                    intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.orders.cancel.single.failure',
                    })
                );
            }
        });
    };

    const { data: materials } = useGetOrderMaterialsQuery(order?.id ?? '', {
        pollingInterval: 30000,
        skipPollingIfUnfocused: true,
        skip: skipPolling || isServiceOrder(order),
    });

    const { data: documents } = useGetOrderDetailsDocumentsQuery(
        isServiceOrder(order)
            ? { orderId: order.id, executionStopId: order.executionStop.id }
            : {
                  orderId: order.id,
                  loadingStopId: order.loadingStop.id,
                  unloadingStopId: order.unloadingStop.id,
              },
        {
            pollingInterval: 30000,
            skipPollingIfUnfocused: true,
            skip: skipPolling || !order?.id,
        }
    );

    const getFooter = () => {
        return (
            <OrderDetailsSidebarFooter
                order={order}
                selectedOrdersCount={selectedOrdersCount}
                handleCancelOrder={() => setShowCancelDialog(true)}
                materials={materials ?? []}
                handlePlanOrder={handlePlanOrder}
                handleDuplicateOrder={handleDuplicateOrder}
                handleEditOrder={handleEditOrder}
            />
        );
    };

    return (
        <Sidebar
            title={intl.formatMessage({
                id: 'intl-msg:ordercom-cloud-frontend.orders.details.title',
            })}
            isOpen={!!order}
            onClose={close}
            footer={getFooter()}
        >
            <CancelDialog
                show={showCancelDialog}
                isLoading={isLoading}
                title={intl.formatMessage(
                    {
                        id: 'intl-msg:ordercom-cloud-frontend.cancel.dialog.order.title',
                    },
                    { ordername }
                )}
                content={intl.formatMessage(
                    {
                        id: 'intl-msg:ordercom-cloud-frontend.cancel.dialog.order.content',
                    },
                    { ordername }
                )}
                onClickConfirm={async () => {
                    await handleCancel(order);
                    setShowCancelDialog(false);
                    close();
                }}
                onClickCancel={() => setShowCancelDialog(false)}
                cancelTrackingKey='ordercom.orders.cancel-dialog.cancel'
                confirmTrackingKey='ordercom.orders.cancel-dialog.confirm'
            />
            <DocumentPollingProvider setIsDocumentDialogOpen={setDocumentDialogState}>
                <OrderDetails order={order} materials={materials} documents={documents} />
            </DocumentPollingProvider>
        </Sidebar>
    );
};
